import axios from 'axios'
import { apiClient } from './api'
import { LoginRequest, LoginResponse, SignupRequest, SignupResponse } from '@/types/api.types'
import { mapUserFromApi } from '@/utils/userMapper'

export const authService = {
  checkHealth: async (): Promise<boolean> => {
    try {
      // Health endpoint is at /health (not /api/v1/health), so use direct axios call
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      const { data } = await axios.get(`${baseUrl}/health`, { timeout: 5000 })
      return data.status === 'healthy'
    } catch (error) {
      return false
    }
  },

  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    // Transform camelCase to snake_case for backend
    const payload = {
      email: credentials.email,
      password: credentials.password,
      remember_me: credentials.rememberMe,
      timezone: credentials.timezone,
      locale: credentials.locale
    }
    const { data } = await apiClient.post<any>('/auth/login', payload)
    // Map user data from snake_case to camelCase
    return {
      token: data.token,
      user: mapUserFromApi(data.user),
      linkedAccounts: data.linked_accounts || []
    }
  },

  signup: async (userData: SignupRequest): Promise<SignupResponse> => {
    const payload = {
      email: userData.email,
      password: userData.password,
      first_name: userData.firstName,
      last_name: userData.lastName,
      phone_number: userData.phoneNumber,
      company_name: userData.companyName
    }
    const { data } = await apiClient.post<any>('/auth/signup', payload)
    // Map user data from snake_case to camelCase
    return {
      token: data.token,
      user: mapUserFromApi(data.user),
      linkedAccounts: data.linked_accounts || []
    }
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout')
  },

  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/auth/forgot-password', { email })
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/reset-password', { token, new_password: newPassword })
  },

  changePassword: async (newPassword: string): Promise<void> => {
    await apiClient.post('/auth/change-password', {
      new_password: newPassword
    })
  },

  getMe: async () => {
    const { data } = await apiClient.get('/auth/me')
    return {
      user: mapUserFromApi(data),
      linkedAccounts: data.linked_accounts || []
    }
  }
}
